import React, {forwardRef, useCallback, useId, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, RadioGroup, Text, useSlotRecipe} from '@chakra-ui/react3'
import Chip from '../chip'
import useHideInitialScrollbar from '../../../hooks/use-hide-initial-scrollbar'
import useNavigation from '../../../hooks/use-navigation'
import {useMaterialVariation} from '../../../hooks/use-variation-attributes'
import {getMasterProductId} from '../../../utils/product-utils'
import materialVariationSelectionRecipe from './index.recipe'

/**
 * The "classic" variation selection: the linked selector products of a product are offered as a
 * dedicated single choice of pills, instead of being grouped into one swatch group per selector.
 *
 * Selecting a pill navigates to the linked product.
 * Products without a selector render nothing, so the color swatches stand on their own.
 */
const MaterialVariationSelection = forwardRef(({css, product, onChange, ...props}, ref) => {
    const recipe = useSlotRecipe({recipe: materialVariationSelectionRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const labelId = useId()
    const navigate = useNavigation()
    const scrollbarRef = useHideInitialScrollbar()
    const materialVariation = useMaterialVariation(product)

    const onValueChange = useCallback(
        ({value}) => {
            const selected = materialVariation?.values?.find((v) => v.value === value)
            if (!selected?.href || value === getMasterProductId(product)) return
            // picking a selector product is an explicit choice, so the configuration stored for it
            // is not offered for restore on arrival
            navigate(selected.href, 'replace', {skipRestoreConfiguration: true})
            onChange?.(value, selected.href)
        },
        [materialVariation, navigate, onChange, product]
    )

    if (!materialVariation?.values?.length) {
        return null
    }

    return (
        <RadioGroup.Root
            ref={ref}
            value={materialVariation.selectedValue.value ?? ''}
            onValueChange={onValueChange}
            aria-labelledby={labelId}
            unstyled
            {...props}
            css={styles.root}
        >
            <Text id={labelId} css={styles.label}>
                <Text as="span">{materialVariation.name}:</Text>
                <Text as="span" css={styles.selectedValue}>
                    {materialVariation.selectedValue.name}
                </Text>
            </Text>

            <Box ref={scrollbarRef} css={styles.chips}>
                {materialVariation.values.map(({name, orderable, value}) => (
                    <RadioGroup.Item key={value} value={value} disabled={!orderable} asChild>
                        <Chip
                            as="label"
                            variant="configurator"
                            data-selected={
                                value === materialVariation.selectedValue.value ? '' : undefined
                            }
                        >
                            <RadioGroup.ItemHiddenInput />
                            {name}
                        </Chip>
                    </RadioGroup.Item>
                ))}
            </Box>
        </RadioGroup.Root>
    )
})

if (process.env.NODE_ENV !== 'production') {
    MaterialVariationSelection.displayName = 'MaterialVariationSelection'
}

MaterialVariationSelection.propTypes = {
    css: PropTypes.object,
    product: PropTypes.object,
    onChange: PropTypes.func
}

export default MaterialVariationSelection
